import { cn } from '@/lib/utils';
import { formatCurrency, formatPnl, formatPercent } from '@/lib/formatters';

export interface PnlDisplayProps {
  value: number | null | undefined;
  showSign?: boolean;
  showPercent?: boolean;
  percentValue?: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function PnlDisplay({
  value,
  showSign = true,
  showPercent,
  percentValue,
  size = 'md',
  className,
}: PnlDisplayProps) {
  const safeValue = value ?? 0;
  const isPositive = safeValue > 0;
  const isNegative = safeValue < 0;

  const sizes = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg font-semibold',
  };

  const colorClass = isPositive
    ? 'text-green-600'
    : isNegative
      ? 'text-red-600'
      : 'text-gray-600';

  return (
    <span className={cn(sizes[size], colorClass, className)}>
      {showSign ? formatPnl(safeValue) : formatCurrency(safeValue)}
      {showPercent && percentValue !== undefined && (
        <span className="ml-1 text-xs opacity-75">({formatPercent(percentValue)})</span>
      )}
    </span>
  );
}

export interface PnlBadgeProps {
  value: number | null | undefined;
  className?: string;
}

export function PnlBadge({ value, className }: PnlBadgeProps) {
  const safeValue = value ?? 0;
  const isPositive = safeValue > 0;
  const isNegative = safeValue < 0;

  const bgClass = isPositive
    ? 'bg-green-100 text-green-700'
    : isNegative
      ? 'bg-red-100 text-red-700'
      : 'bg-gray-100 text-gray-700';

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium',
        bgClass,
        className
      )}
    >
      {formatPnl(safeValue)}
    </span>
  );
}
